
import axios from 'axios'

export default {
  name: 'Carousel',
  data () {
    return {
      slideList: [],
      loading: true, // banner 数据加载中：显示骨架占位，避免空白
      displayIndex: 1, // 当前显示索引（包含克隆项，初始为1）
      realIndex: 0, // 真实索引（用于指示点）
      timer: null,
      touchStartX: 0,
      touchCurrentX: 0,
      isDragging: false,
      // 横滑拦截：touchend 会先于移动端合成 click 复位 isDragging，单靠它防不住
      // "滑动切换误触跳转"。swiped 在位移超阈值时置位并保持到下一次 touchstart——
      // 滑动后的合成 click 一律不触发跳转（桌面端无 touch，恒 false 不受影响）
      swiped: false,
      offset: 0,
      skipTransition: false
    }
  },
  computed: {
    // 显示列表：多张时首尾各添加一个克隆项实现无限循环；1 张原样返回（不克隆——
    // 克隆会让同一张图重复出 3 份 DOM，静态 banner 无循环语义）
    displayList () {
      if (this.slideList.length <= 1) return this.slideList
      const first = { ...this.slideList[0], _isClone: true }
      const last = { ...this.slideList[this.slideList.length - 1], _isClone: true }
      return [last, ...this.slideList, first]
    },
    // 轮播交互（触摸滑动/过渡衔接）只在多张时绑定；1 张时空对象 = 纯静态图
    trackEvents () {
      if (this.slideList.length <= 1) return {}
      return {
        touchstart: this.onTouchStart,
        touchmove: this.onTouchMove,
        touchend: this.onTouchEnd,
        transitionend: this.onTransitionEnd
      }
    },
    trackStyle () {
      // 1 张：固定显示唯一一张（displayIndex 克隆位语义不适用，-100% 会把它移出视口）
      if (this.slideList.length <= 1) {
        return { transform: 'translateX(0)', transition: 'none' }
      }
      let translateX = -(this.displayIndex * 100)
      // 拖动时的偏移
      if (this.isDragging && this.offset !== 0) {
        translateX += (this.offset / 375) * 100
      }
      return {
        transform: `translateX(${translateX}%)`,
        transition: this.skipTransition || this.isDragging ? 'none' : 'transform 0.5s ease-in-out'
      }
    }
  },
  methods: {
    getImageUrl (image) {
      if (!image) return ''
      if (this.$img) return this.$img(image)
      if (image.startsWith('http') || image.startsWith('data:')) return image
      return image
    },
    getRealItem (displayIdx) {
      if (displayIdx === 0) return this.slideList[this.slideList.length - 1]
      if (displayIdx === this.displayList.length - 1) return this.slideList[0]
      return this.slideList[displayIdx - 1]
    },
    handleClick (item) {
      if (this.isDragging || this.swiped) return // Mobile 滑动切换中/滑动后合成 click 不触发跳转
      if (!item || !item.url) return
      if (item.type === 'product') {
        // url 字段即 product_id（管理端配置约定），详情页取参 query.id
        this.$router.push(`/details?id=${item.url}`)
      } else if (item.type === 'page') {
        // 站内路由（如 /help/how-to-order 教程入口）：只认 '/' 开头——防配置手误，
        // 也天然拒绝 javascript:/data: 等危险 scheme（不会进入 push 出站）
        if (typeof item.url === 'string' && item.url.startsWith('/')) {
          this.$router.push(item.url)
        }
      } else if (typeof item.url === 'string' && /^https?:\/\//.test(item.url)) {
        // 外部链接：严格 http(s) scheme + noopener/noreferrer（防 reverse tabnabbing）
        window.open(item.url, '_blank', 'noopener,noreferrer')
      }
      // 其余形态（含未知 scheme）无动作：配置源可信 + fail-safe 双保险
    },
    goTo (index) {
      this.skipTransition = false
      this.displayIndex = index + 1
      this.realIndex = index
    },
    next () {
      if (this.slideList.length <= 1) return
      // 后台标签页不推进：渲染被节流时 transitionend 不会触发，纯积累漂移
      if (document.hidden) return
      this.skipTransition = false
      this.displayIndex++
      this.driftGuard()
      this.updateRealIndex()
    },
    prev () {
      if (this.slideList.length <= 1) return
      this.skipTransition = false
      this.displayIndex--
      this.driftGuard()
      this.updateRealIndex()
    },
    // 漂移保护（2026-09-17 修多 banner 空图）：displayIndex 只允许 [1, len+1]。
    // 到达克隆项后靠 transitionend 无动画跳回；但后台标签页/渲染节流下 transitionend
    // 可能丢失 → next() 无限累加 → track 平移出全部 slide = banner 区整块空白。
    // 越界立即无动画归位到同相位位置（模运算保持指向同一张图）。
    driftGuard () {
      const len = this.slideList.length
      if (len === 0) return
      if (this.displayIndex > len + 1 || this.displayIndex < 0) {
        this.skipTransition = true
        this.displayIndex = ((this.displayIndex - 1) % len + len) % len + 1
      }
    },
    updateRealIndex () {
      const len = this.slideList.length
      if (len === 0) return
      this.realIndex = ((this.displayIndex - 1) % len + len) % len
    },
    onTransitionEnd (e) {
      // 只认 track 本体的 transform 过渡（子元素过渡事件冒泡不处理）
      if (e && (e.target !== e.currentTarget || e.propertyName !== 'transform')) return
      // 无缝循环：到达克隆项时先禁用动画，下个 tick 再瞬间跳转
      if (this.displayIndex === 0) {
        this.skipTransition = true
        this.$nextTick(() => {
          this.displayIndex = this.slideList.length
          this.realIndex = this.slideList.length - 1
        })
      } else if (this.displayIndex === this.slideList.length + 1) {
        this.skipTransition = true
        this.$nextTick(() => {
          this.displayIndex = 1
          this.realIndex = 0
        })
      }
    },
    startAutoPlay () {
      // 1 张无轮播语义：不启 timer（4s 后"切"到同一张只是无意义的 transform 闪跳）
      if (this.slideList.length <= 1) return
      this.stopAutoPlay()
      this.timer = setInterval(() => this.next(), 4000)
    },
    stopAutoPlay () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    onTouchStart (e) {
      this.touchStartX = e.touches[0].clientX
      this.touchCurrentX = this.touchStartX
      this.isDragging = true
      this.swiped = false
      this.offset = 0
      this.stopAutoPlay()
    },
    onTouchMove (e) {
      if (!this.isDragging) return
      // 原模板 @touchmove.prevent 移入 handler（trackEvents 对象语法不支持修饰符），
      // 拖动期间阻止页面纵向滚动跟随，多张行为不变
      e.preventDefault()
      this.touchCurrentX = e.touches[0].clientX
      this.offset = this.touchCurrentX - this.touchStartX
      // 位移超 10px 视为滑动意图（tap 微动不算），touchend 后的合成 click 拦截
      if (Math.abs(this.offset) > 10) this.swiped = true
    },
    onTouchEnd () {
      if (!this.isDragging) return
      this.isDragging = false

      const diff = this.touchStartX - this.touchCurrentX
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.next()
        } else {
          this.prev()
        }
      }
      this.offset = 0
      this.startAutoPlay()
    },
    async fetchBanners () {
      // Banner 数量自适应（2026-09-12）：运营配置几张展示几张——0 张 = 模块不渲染（模板层 v-if），
      // 1 张 = 静态展示，2+ = 正常轮播。不再回退默认三图。
      try {
        const res = await axios.get('config/home-banners')
        const list = (res.data.code === 200 && Array.isArray(res.data.data)) ? res.data.data : []
        // 有效 banner：对象存在 + image 为非空字符串 + enabled !== false（旧数据无 enabled 视为启用）。
        // 空串/null/失效配置不计数，也不触发默认补图
        this.slideList = list.filter(b => b && typeof b.image === 'string' && b.image.trim() !== '' && b.enabled !== false)
        // 获取数据后重置显示索引
        this.displayIndex = 1
        this.realIndex = 0
      } catch (e) {
        this.slideList = []
        this.displayIndex = 1
        this.realIndex = 0
      } finally {
        this.loading = false
        // 数据落地后按数量收敛 autoplay：多张启（覆盖 activated refetch 场景——同步段
        // startAutoPlay 时 fetch 未回会漏启），≤1 张停（清掉旧多张数据留下的 timer）
        if (this.slideList.length > 1) this.startAutoPlay()
        else this.stopAutoPlay()
      }
    }
  },
  created () {
    this.fetchBanners()
  },
  mounted () {
    this.startAutoPlay()
  },
  activated () {
    // keepAlive 返回必拉新（2026-09-19 Banner 残留修复）：banner 真值无缓存层
    // （system_config 直读的轻查询），此前仅空列表才 refetch —— 后台删掉 banner 后
    // 返回首页永远显示 keepAlive 缓存里的旧数据。数据落地后 fetchBanners 会重置
    // displayIndex/realIndex（active index 校正：删的是当前展示项也回到第 1 张）
    this.fetchBanners()
    // 重置显示索引，避免显示灰色；skipTransition 抑制回页瞬间 0.5s 飞移动画
    this.skipTransition = true
    this.displayIndex = 1
    this.realIndex = 0
    this.startAutoPlay()
  },
  deactivated () {
    // 离开页面时停止自动播放
    this.stopAutoPlay()
  },
  beforeDestroy () {
    this.stopAutoPlay()
  }
}
